import { useAuth } from '@/contexts/auth-context';
import {
  useBillingSettingsScope,
  type BillingSettingsScope,
} from '@/hooks/actions/use-billing-settings-scope';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import type {
  ApiResponse,
  OrganizationBillingContext,
  OrganizationBillingSettingsData,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const BILLING_SETTINGS_QUERY_KEY = ['billingSettings'] as const;
export const billingSettingsQueryKey = (scope: BillingSettingsScope) =>
  [
    ...BILLING_SETTINGS_QUERY_KEY,
    scope.userId,
    scope.organizationId,
    scope.brandId,
    scope.role,
    scope.brandRole,
  ] as const;

const settingsSchema = z
  .object({
    organizationId: z.string().min(1),
    brandId: z.string().min(1),
    canManage: z.boolean(),
    billingEmail: z.string().email().max(254).nullable(),
    portalAvailable: z.boolean(),
    canCloseOrganization: z.boolean(),
    closedAt: z.string().datetime({ offset: true }).nullable(),
    paymentRecovery: z
      .object({
        status: z.enum(['retrying', 'unpaid']),
        nextRetryAt: z.string().datetime({ offset: true }).nullable(),
        invoices: z.array(
          z
            .object({
              id: z.string().min(1),
              number: z.string().min(1).nullable(),
              amountRemaining: z.number().finite().int().nonnegative().refine(Number.isSafeInteger),
              currency: z.enum(['EUR', 'USD', 'GBP', 'CAD']),
            })
            .strict()
        ),
        disputes: z
          .array(
            z
              .object({
                id: z.string().min(1),
                number: z.string().min(1).nullable(),
                amountRemaining: z.number().finite().int().positive().refine(Number.isSafeInteger),
                currency: z.enum(['EUR', 'USD', 'GBP', 'CAD']),
              })
              .strict()
          )
          .optional(),
      })
      .strict()
      .nullable(),
  })
  .strict();

export function readBillingSettings(value: unknown): OrganizationBillingSettingsData | undefined {
  const parsed = settingsSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

export async function fetchBillingSettings(
  scope: BillingSettingsScope,
  assertCurrent: (scope: BillingSettingsScope) => void,
  signal?: AbortSignal
): Promise<OrganizationBillingSettingsData> {
  assertCurrent(scope);
  if (!scope.userId || !scope.organizationId || !scope.brandId)
    throw new Error('No authorized billing settings scope');
  const response = await api.get<ApiResponse<unknown>>('/dashboard/billing/settings', { signal });
  assertCurrent(scope);
  const data = readBillingSettings(response.data.data);
  if (
    !response.data.success ||
    !data ||
    data.organizationId !== scope.organizationId ||
    data.brandId !== scope.brandId
  )
    throw new Error('Invalid organization billing settings');
  return data;
}

export function useBillingSettingsQuery({
  context,
  enabled = true,
  pollPaymentRecovery = false,
}: {
  context?: OrganizationBillingContext;
  enabled?: boolean;
  pollPaymentRecovery?: boolean;
}) {
  const { isLoading } = useAuth();
  const { scope, assertCurrent } = useBillingSettingsScope();
  const current = readBillingContext(context);
  const canRead =
    !!scope.userId &&
    !!current?.capabilities.canReadBilling &&
    current.organizationId === scope.organizationId &&
    current.brandId === scope.brandId;
  return useQuery({
    queryKey: billingSettingsQueryKey(scope),
    enabled: enabled && !isLoading && canRead,
    queryFn: ({ signal }) => fetchBillingSettings(scope, assertCurrent, signal),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      pollPaymentRecovery && query.state.data?.paymentRecovery ? 30_000 : false,
  });
}
